/**
 * Spotify Web Playback SDK Player
 * 
 * Enables full track playback for Spotify Premium users
 * Falls back to preview for free users
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { spotifyAuth } from '../services/spotify';
import './SpotifyPlayer.css';

// Track if SDK script is loaded
let isSDKLoaded = false;
let sdkLoadPromise = null;

function SpotifyPlayer({ 
  trackUri, 
  currentTrack,
  onTrackEnd,
  onNext,
  onPrevious,
  queueLength = 0,
  currentIndex = 0,
}) {
  const [player, setPlayer] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isPaused, setIsPaused] = useState(true);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(50);
  const [error, setError] = useState(null);
  const [isPremium, setIsPremium] = useState(null);
  const [sessionEstablished, setSessionEstablished] = useState(false);
  
  const positionInterval = useRef(null);
  const isRefreshingToken = useRef(false);
  const lastAuthErrorTime = useRef(0);
  const lastPlayedUri = useRef(null); // Track last successfully played URI to prevent re-playing
  const activeDeviceIdRef = useRef(null); // Track the device ID we're actively using for playback
  const isPlayingRef = useRef(false); // Track if we're currently attempting to play

  // Load Spotify Web Playback SDK
  const loadSDK = useCallback(() => {
    if (isSDKLoaded) return Promise.resolve();
    if (sdkLoadPromise) return sdkLoadPromise;

    sdkLoadPromise = new Promise((resolve) => {
      // Define callback before loading script
      window.onSpotifyWebPlaybackSDKReady = () => {
        console.log('✅ Spotify Web Playback SDK Ready');
        isSDKLoaded = true;
        resolve();
      };

      // Check if already loaded
      if (window.Spotify) {
        isSDKLoaded = true;
        resolve();
        return;
      }

      // Load the SDK script
      const script = document.createElement('script');
      script.src = 'https://sdk.scdn.co/spotify-player.js';
      script.async = true;
      document.body.appendChild(script);
    });

    return sdkLoadPromise;
  }, []);

  // Check if user has Premium
  useEffect(() => {
    const checkPremium = async () => {
      try {
        const profile = await spotifyAuth.getUserProfile();
        setIsPremium(profile.product === 'premium');
        if (profile.product !== 'premium') {
          setError('Spotify Premium required for full playback');
        }
      } catch (err) {
        console.error('Error checking premium status:', err);
      }
    };
    checkPremium();
  }, []);

  // Initialize player
  useEffect(() => {
    if (!isPremium) return;

    let playerInstance = null;

    const initPlayer = async () => {
      await loadSDK();

      const token = spotifyAuth.getAccessToken();
      if (!token) {
        setError('Not authenticated with Spotify');
        return;
      }

      playerInstance = new window.Spotify.Player({
        name: 'Jookbox Orchestrator',
        getOAuthToken: cb => {
          const currentToken = spotifyAuth.getAccessToken();
          cb(currentToken);
        },
        volume: volume / 100
      });

      // Error handling
      playerInstance.addListener('initialization_error', ({ message }) => {
        console.error('Spotify init error:', message);
        setError('Failed to initialize player');
      });

      playerInstance.addListener('authentication_error', async ({ message }) => {
        console.error('Spotify auth error:', message);
        
        // Debounce: Don't refresh if we just tried recently (within last 15 seconds)
        const now = Date.now();
        if (isRefreshingToken.current || (now - lastAuthErrorTime.current < 15000)) {
          // Silently skip to avoid console spam
          return;
        }
        
        isRefreshingToken.current = true;
        lastAuthErrorTime.current = now;
        
        // Try to refresh the token
        try {
          console.log('🔄 Attempting to refresh Spotify token...');
          await spotifyAuth.refreshAccessToken();
          console.log('✅ Token refreshed, player should reconnect');
          // Clear error - player will reconnect with new token
          setError(null);
        } catch (refreshErr) {
          console.error('Failed to refresh token:', refreshErr);
          // Don't set error immediately - might be temporary
          // Only set error if multiple failures occur
        } finally {
          // Reset after a delay to allow for retries if needed
          setTimeout(() => {
            isRefreshingToken.current = false;
          }, 10000); // 10 second cooldown
        }
      });

      playerInstance.addListener('account_error', ({ message }) => {
        console.error('Spotify account error:', message);
        setError('Spotify Premium required for playback');
      });

      playerInstance.addListener('playback_error', ({ message }) => {
        console.error('Spotify playback error:', message);
      });

      // Ready
      playerInstance.addListener('ready', async ({ device_id }) => {
        console.log('✅ Spotify Player ready, device ID:', device_id);
        
        // Only update device ID if it's different and we're not currently playing
        // This prevents the play effect from re-running mid-playback
        setDeviceId(prevId => {
          if (prevId === device_id) {
            return prevId; // No change, avoid re-render
          }
          // Only update if we're not currently attempting to play
          // This prevents device ID changes from interrupting playback
          if (!isPlayingRef.current) {
            console.log('🔄 Device ID changed:', prevId, '→', device_id);
            activeDeviceIdRef.current = device_id;
            // Reset session when device ID changes (new session needed)
            setSessionEstablished(false);
            return device_id;
          } else {
            console.log('⚠️ Device ID changed during playback attempt, keeping previous:', prevId);
            // During playback, keep the previous device ID to avoid interrupting playback
            // The new device ID will be used on the next track
            // Don't update activeDeviceIdRef - keep using the one we locked in
            return prevId; // Keep previous to avoid interrupting playback and triggering re-renders
          }
        });
        
        // Wait a moment for SDK to establish WebSocket connection
        // Don't set sessionEstablished here - it will be set when we successfully play
        // This prevents the play effect from re-running unnecessarily
        setTimeout(() => {
          console.log('✅ Player device ready');
          setIsReady(true);
          setError(null);
          // Only set sessionEstablished if we've already played something successfully
          // Otherwise, it will be set on first successful play
          if (lastPlayedUri.current) {
            setSessionEstablished(true);
          }
        }, 300); // Give SDK 300ms to establish connection
      });

      // Not Ready
      playerInstance.addListener('not_ready', ({ device_id }) => {
        console.log('⚠️ Spotify Player not ready:', device_id);
        setIsReady(false);
        setSessionEstablished(false);
        // Don't clear deviceId - it might reconnect with the same ID
        // The player will emit 'ready' again when it reconnects
      });

      // State changes
      playerInstance.addListener('player_state_changed', (state) => {
        if (!state) return;

        setIsPaused(state.paused);
        setCurrentPosition(state.position);
        setDuration(state.duration);

        // Track ended
        if (state.paused && state.position === 0 && state.track_window.previous_tracks.length > 0) {
          // Clear last played URI and reset session so next track can play
          lastPlayedUri.current = null;
          isPlayingRef.current = false;
          setSessionEstablished(false); // Reset session so it re-establishes for next track
          onTrackEnd && onTrackEnd();
        }
      });

      // Connect
      const connected = await playerInstance.connect();
      if (connected) {
        console.log('✅ Connected to Spotify');
        setPlayer(playerInstance);
      } else {
        setError('Failed to connect to Spotify');
      }
    };

    initPlayer();

    // Cleanup
    return () => {
      if (positionInterval.current) {
        clearInterval(positionInterval.current);
      }
      if (playerInstance) {
        playerInstance.disconnect();
      }
    };
  }, [isPremium, loadSDK, volume, onTrackEnd]);

  // Handle window focus/blur to maintain Spotify session
  useEffect(() => {
    if (!player || !isPremium) return;

    const handleFocus = async () => {
      // Wait a moment for the browser to fully regain focus
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Check if player is still ready
      if (player) {
        try {
          const state = await player.getCurrentState();
          if (state) {
            // Session is still active, ensure we're marked as ready
            setIsReady(prev => prev ? prev : true);
            setSessionEstablished(prev => prev ? prev : true);
          } else {
            // Session is inactive, will be re-established on next play
            setSessionEstablished(false);
          }
        } catch (err) {
          // Silently handle - will be re-established on next play
          setSessionEstablished(false);
        }
      }
    };

    const handleBlur = () => {
      // Window lost focus - session may become inactive
      // Will be checked and re-established when focus returns or on next play
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [player, isPremium]); // Removed isReady and sessionEstablished from deps to prevent re-running

  // Play track when URI changes
  useEffect(() => {
    if (!deviceId || !trackUri || !isReady || !player) return;
    
    console.log('🎵 SpotifyPlayer useEffect - trackUri changed:', trackUri, 'currentTrack:', currentTrack?.Title);
    
    // CRITICAL: Clear lastPlayedUri FIRST if switching to a different track
    // This must happen before any checks to ensure we always play the correct track
    if (lastPlayedUri.current && lastPlayedUri.current !== trackUri) {
      console.log('🔄 Track changed - clearing previous:', lastPlayedUri.current, '→', trackUri);
      lastPlayedUri.current = null;
      isPlayingRef.current = false; // Reset playing flag when switching tracks
    }
    
    // EARLY CHECK: Only skip if we're currently attempting to play the SAME track
    // This prevents re-runs during active playback attempts, but allows retries if playback failed
    if (isPlayingRef.current && lastPlayedUri.current === trackUri) {
      console.log('⏸️ Already attempting to play this track, skipping...');
      return;
    }
    
    // Use a ref to track the current track URI being processed
    // This prevents race conditions when trackUri changes rapidly
    const currentTrackUriRef = { value: trackUri };

    const playTrack = async () => {
      // Prevent re-running if we're already attempting to play the same track
      // But only if we're actually in the middle of an attempt (isPlayingRef is true)
      if (isPlayingRef.current && lastPlayedUri.current === trackUri) {
        console.log('⏸️ Already attempting to play this track, skipping...');
        return;
      }
      
      // If lastPlayedUri is set but different from current trackUri, we're switching tracks
      // Make sure we clear it (should already be cleared above, but double-check)
      if (lastPlayedUri.current && lastPlayedUri.current !== trackUri) {
        console.log('🔄 Clearing previous track reference:', lastPlayedUri.current);
        lastPlayedUri.current = null;
        isPlayingRef.current = false;
      }
      
      // Check if trackUri has changed since we started (race condition protection)
      if (currentTrackUriRef.value !== trackUri) {
        console.log('⚠️ Track URI changed during playback attempt, aborting...');
        isPlayingRef.current = false;
        return;
      }
      
      // Mark that we're attempting to play and lock in the device ID
      isPlayingRef.current = true;
      activeDeviceIdRef.current = deviceId; // Lock in the device ID for this playback attempt
      
      console.log('🎵 Starting playback for:', trackUri);
      
      // Always check current player state and pause if playing a different track
      try {
        const currentState = await player.getCurrentState();
        if (currentState && !currentState.paused) {
          const currentTrackUri = currentState.track_window?.current_track?.uri;
          // If we're already playing this exact track, don't re-play or pause
          if (currentTrackUri === trackUri) {
            console.log('✅ Track is already playing, skipping playback attempt...');
            isPlayingRef.current = false;
            lastPlayedUri.current = trackUri; // Update to reflect current state
            return;
          }
          // If we're switching to a different track, pause the current one first
          if (currentTrackUri && currentTrackUri !== trackUri) {
            console.log('⏸️ Pausing current track before switching...', currentTrackUri, '→', trackUri);
            try {
              await player.pause();
              // Give it a moment to stop
              await new Promise(resolve => setTimeout(resolve, 100));
            } catch (pauseErr) {
              console.log('⚠️ Could not pause current track:', pauseErr);
            }
          }
        } else if (currentState && currentState.paused && currentState.track_window?.current_track?.uri === trackUri) {
          // Track is paused but it's the same track - resume it instead of re-playing
          console.log('▶️ Track is paused, resuming...');
          try {
            await player.resume();
            isPlayingRef.current = false;
            lastPlayedUri.current = trackUri;
            return;
          } catch (resumeErr) {
            console.log('⚠️ Could not resume, will try to play:', resumeErr);
            // Continue with normal playback if resume fails
          }
        }
      } catch (err) {
        // If we can't check state, continue with playback attempt
        console.log('⚠️ Could not check player state, will attempt to play anyway:', err);
      }
      
      // Double-check trackUri hasn't changed (race condition protection)
      if (currentTrackUriRef.value !== trackUri) {
        console.log('⚠️ Track URI changed after state check, aborting...');
        isPlayingRef.current = false;
        return;
      }
      
      const token = spotifyAuth.getAccessToken();
      if (!token) {
        setError('Not authenticated with Spotify');
        isPlayingRef.current = false; // Reset playing flag before returning
        return;
      }
      
      try {
        // Check if player is still connected and active
        // This is especially important after focus loss
        let playerActive = false;
        try {
          const currentState = await player.getCurrentState();
          playerActive = currentState !== null;
          if (!playerActive && !sessionEstablished) {
            console.log('🔄 Player session may be inactive, checking connection...');
            // Try to reconnect the player if it's disconnected
            if (player) {
              const reconnected = await player.connect();
              if (reconnected) {
                console.log('✅ Player reconnected');
                // Wait for ready event
                await new Promise(resolve => setTimeout(resolve, 1000));
                // Update sessionEstablished after reconnection
                setSessionEstablished(true);
              }
            }
          } else if (playerActive) {
            // Player is active, ensure session is marked as established
            if (!sessionEstablished) {
              setSessionEstablished(true);
            }
          }
        } catch (stateErr) {
          console.log('⚠️ Could not get player state, will attempt to play anyway:', stateErr);
          // Continue anyway - might work
        }
        // Quick check if device is available (non-blocking - we'll still try to play if not found)
        // The device might work even if it's not in the list yet
        try {
          const devicesResponse = await fetch('https://api.spotify.com/v1/me/player/devices', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (devicesResponse.ok) {
            const devices = await devicesResponse.json();
            const deviceExists = devices.devices?.some(d => d.id === deviceId);
            // Only log if device not found (unexpected)
            if (!deviceExists) {
              // Device not in list, but will try to play anyway - this is often normal
            }
          }
        } catch (err) {
          // Silently continue - device check is non-critical
        }
        
        // Check if there's an active player session
        const stateCheckResponse = await fetch('https://api.spotify.com/v1/me/player', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        let needsTransfer = false;
        
        // Handle different response statuses
        if (stateCheckResponse.status === 204) {
          // No active playback, but session exists - try to transfer
          needsTransfer = true;
        } else if (stateCheckResponse.status === 404) {
          // No active player session at all - try playing directly (this may activate the session)
          needsTransfer = false;
        } else if (stateCheckResponse.ok) {
          // There's an active session, check if our device is active
          // Safely parse JSON - response might be empty
          try {
            const contentType = stateCheckResponse.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const state = await stateCheckResponse.json().catch(() => null);
              if (state && state.device) {
                if (state.device.id !== deviceId) {
                  needsTransfer = true;
                }
                // If same device, no transfer needed
              } else {
                // No device info, need to transfer
                needsTransfer = true;
              }
            } else {
              // No JSON content, need to transfer
              needsTransfer = true;
            }
          } catch (jsonErr) {
            // Could not parse player state, will try transfer
            needsTransfer = true;
          }
        } else {
          // Other error, try transfer anyway
          needsTransfer = true;
        }

        // Always ensure device is active before playing (especially important when switching tracks)
        // Use the active device ID ref to ensure consistency (prevents using changing device IDs)
        let deviceToUse = activeDeviceIdRef.current || deviceId;
        console.log('🎵 Ensuring device is active...', deviceToUse);
        
        // First verify the device exists, and if not, try to reconnect or use current deviceId
        let deviceExists = false;
        try {
          const devicesCheckResponse = await fetch('https://api.spotify.com/v1/me/player/devices', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (devicesCheckResponse.ok) {
            const devicesData = await devicesCheckResponse.json();
            const availableDevices = devicesData.devices || [];
            deviceExists = availableDevices.some(d => d.id === deviceToUse);
            
            if (!deviceExists) {
              console.warn('⚠️ Device not found in available devices:', deviceToUse);
              console.log('Available devices:', availableDevices.map(d => ({ id: d.id, name: d.name, is_active: d.is_active })));
              
              // Try to use the current deviceId from player state (should be the connected device)
              if (deviceId && availableDevices.some(d => d.id === deviceId)) {
                console.log('🔄 Using current deviceId from player state:', deviceId);
                deviceToUse = deviceId;
                activeDeviceIdRef.current = deviceId;
                deviceExists = true;
              } else if (availableDevices.length > 0) {
                // Use the first active device, or first device if none are active
                const activeDevice = availableDevices.find(d => d.is_active) || availableDevices[0];
                console.log('🔄 Switching to available device:', activeDevice.id, activeDevice.name, '(was:', deviceToUse, ')');
                deviceToUse = activeDevice.id;
                activeDeviceIdRef.current = activeDevice.id;
                // Update state to keep it in sync
                setDeviceId(activeDevice.id);
                deviceExists = true;
              } else {
                console.error('❌ No available devices found');
                setError('No Spotify devices available. Please ensure Spotify is open and try again.');
                isPlayingRef.current = false;
                lastPlayedUri.current = null;
                return;
              }
            }
          }
        } catch (devicesErr) {
          console.warn('⚠️ Could not verify device existence:', devicesErr);
          // Continue anyway - might be a temporary network issue
        }
        
        const transferResponse = await fetch('https://api.spotify.com/v1/me/player', {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            device_ids: [deviceToUse],
            play: false // Don't auto-play, we'll do it ourselves
          })
        });

        // 404 on transfer means no active session - wait and retry
        if (transferResponse.status === 404) {
          console.log('⚠️ No active session yet, waiting for session to establish...');
          // Wait for session to be established (SDK needs time to connect)
          await new Promise(resolve => setTimeout(resolve, 400)); // Reduced from 800ms
          
          // Try transfer again - use locked device ID
          const retryTransferResponse = await fetch('https://api.spotify.com/v1/me/player', {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              device_ids: [activeDeviceIdRef.current || deviceId],
              play: false
            })
          });
          
          if (retryTransferResponse.status === 404) {
            console.log('⚠️ Session still not established, will try direct play anyway');
          } else if (retryTransferResponse.ok || retryTransferResponse.status === 204) {
            console.log('✅ Session established after retry');
            await new Promise(resolve => setTimeout(resolve, 200)); // Reduced from 300ms
          }
        } else if (!transferResponse.ok && transferResponse.status !== 204) {
          const transferError = await transferResponse.json().catch(() => ({}));
          console.error('Device transfer failed:', transferResponse.status, transferError);
          
          // Verify device is still available
          const devicesResponse = await fetch('https://api.spotify.com/v1/me/player/devices', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (devicesResponse.ok) {
            const devices = await devicesResponse.json();
            const deviceExists = devices.devices?.some(d => d.id === deviceId);
            if (!deviceExists) {
              console.error('Device no longer exists, player may need to reconnect');
              setError('Player disconnected. Please wait a moment and try again.');
              isPlayingRef.current = false;
              lastPlayedUri.current = null;
              return;
            }
          }
        } else {
          // Transfer successful (or already active), wait for device to be ready
          console.log('✅ Device activated, waiting for activation...');
          await new Promise(resolve => setTimeout(resolve, 200)); // Reduced wait time
          
          // Verify device is now active
          const verifyResponse = await fetch('https://api.spotify.com/v1/me/player', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (verifyResponse.ok) {
            try {
              const verifyState = await verifyResponse.json();
              if (verifyState.device && verifyState.device.id === deviceId) {
                console.log('✅ Device verified as active');
              } else {
                console.log('⚠️ Device may not be active yet, continuing anyway...');
              }
            } catch (err) {
              // Response might be empty, that's OK
              console.log('⚠️ Could not verify device state, continuing...');
            }
          }
        }

        // Now play the track - use the locked device ID (reuse deviceToUse from above)
        console.log('🎵 Playing track:', trackUri, 'on device:', deviceToUse);
        console.log('📊 Playback attempt - deviceToUse:', deviceToUse, 'activeDeviceIdRef:', activeDeviceIdRef.current, 'deviceId:', deviceId);
        
        // Add a timeout to the fetch to prevent hanging
        const fetchTimeout = 10000; // 10 second timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), fetchTimeout);
        
        let playResponse;
        try {
          playResponse = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceToUse}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              uris: [trackUri]
            }),
            signal: controller.signal
          });
          clearTimeout(timeoutId);
        } catch (fetchErr) {
          clearTimeout(timeoutId);
          if (fetchErr.name === 'AbortError') {
            console.error('❌ Play request timed out after 10 seconds');
            setError('Playback request timed out. Please try again.');
            isPlayingRef.current = false;
            lastPlayedUri.current = null;
              return;
          }
          throw fetchErr; // Re-throw other errors
        }

        if (playResponse.ok || playResponse.status === 204) {
          // Success! Track should be playing
          console.log('✅ Track playing! Status:', playResponse.status);
          setError(null);
          setSessionEstablished(true);
          lastPlayedUri.current = trackUri;
          isPlayingRef.current = false; // Reset playing flag on success
          
          // Explicitly resume the player to ensure it continues playing
          // The REST API call starts playback, but the Web Playback SDK player state might be out of sync
          try {
            await new Promise(resolve => setTimeout(resolve, 500)); // Wait longer for state to sync
            const currentState = await player.getCurrentState();
            console.log('🔍 Player state after API call:', {
              hasState: !!currentState,
              paused: currentState?.paused,
              trackUri: currentState?.track_window?.current_track?.uri,
              expectedUri: trackUri,
              position: currentState?.position,
              duration: currentState?.duration
            });
            
            if (currentState) {
              // Update local state to match actual player state
              setIsPaused(currentState.paused);
              setCurrentPosition(currentState.position || 0);
              setDuration(currentState.duration || 0);
              
              if (currentState.paused) {
                console.log('▶️ Player state shows paused, resuming...');
                await player.resume();
                // Wait a bit and check again
                await new Promise(resolve => setTimeout(resolve, 300));
                const stateAfterResume = await player.getCurrentState();
                if (stateAfterResume) {
                  setIsPaused(stateAfterResume.paused);
                  console.log('🔍 Player state after resume:', {
                    paused: stateAfterResume.paused,
                    position: stateAfterResume.position
                  });
                }
              } else {
                console.log('✅ Player is already playing');
              }
            } else {
              console.warn('⚠️ Player state is null - player may not be connected');
            }
          } catch (resumeErr) {
            console.log('⚠️ Could not check/resume player state:', resumeErr);
            // Continue anyway - playback might still be working
          }
          
          return; // Exit early on success
        }
        
        // If we get here, playback failed
        const errorData = await playResponse.json().catch(() => ({}));
        console.error('❌ Play failed:', playResponse.status, errorData);
        
        // If 404, device might be disconnected - reconnect and retry
        if (playResponse.status === 404) {
          console.log('⏳ 404 error - device may be disconnected, attempting reconnection...');
          
          // First, check if device still exists
          let currentDeviceId = activeDeviceIdRef.current || deviceId;
          try {
            const devicesResponse = await fetch('https://api.spotify.com/v1/me/player/devices', {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (devicesResponse.ok) {
              const devicesData = await devicesResponse.json();
              const availableDevices = devicesData.devices || [];
              const ourDevice = availableDevices.find(d => d.id === currentDeviceId);
              
              if (!ourDevice) {
                console.log('⚠️ Our device not found in available devices, player may need to reconnect');
                // Try to reconnect the player
                if (player) {
                  try {
                    console.log('🔄 Attempting to reconnect player to get new device ID...');
                    const reconnected = await player.connect();
                    if (reconnected) {
                      console.log('✅ Player reconnected, waiting for new device ID...');
                      // Wait for ready event to fire and device to be available
                      await new Promise(resolve => setTimeout(resolve, 1000));
                      // Check if device ID was updated
                      currentDeviceId = activeDeviceIdRef.current || deviceId;
                      console.log('🔄 New device ID after reconnect:', currentDeviceId);
                    } else {
                      console.log('⚠️ Player reconnect returned false');
                    }
                  } catch (reconnectErr) {
                    console.error('❌ Error reconnecting player:', reconnectErr);
                  }
                }
              } else {
                console.log('✅ Device still exists, but may not be active');
              }
            }
          } catch (devicesErr) {
            console.log('⚠️ Could not check devices:', devicesErr);
          }
          
          // Try to transfer playback to our device (use current device ID)
          try {
            const transferResponse = await fetch('https://api.spotify.com/v1/me/player', {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                device_ids: [currentDeviceId]
              })
            });
            
            if (transferResponse.ok || transferResponse.status === 204) {
              console.log('✅ Device transfer successful, waiting before retry...');
              await new Promise(resolve => setTimeout(resolve, 500));
            } else {
              const transferError = await transferResponse.json().catch(() => ({}));
              console.log('⚠️ Device transfer failed:', transferResponse.status, transferError);
            }
          } catch (transferErr) {
            console.log('⚠️ Device transfer error:', transferErr);
          }
          
          // Retry with exponential backoff (up to 3 attempts)
          let retryAttempt = 0;
          const maxRetries = 3;
          let success = false;
          
          while (retryAttempt < maxRetries && !success) {
            retryAttempt++;
            const waitTime = 500 * retryAttempt; // 500ms, 1000ms, 1500ms
            if (retryAttempt > 1) {
              console.log(`⏳ Retry attempt ${retryAttempt}/${maxRetries} after ${waitTime}ms...`);
              await new Promise(resolve => setTimeout(resolve, waitTime));
            }
            
            // Check if trackUri changed during retry
            if (currentTrackUriRef.value !== trackUri) {
              console.log('⚠️ Track URI changed during retry, aborting...');
              isPlayingRef.current = false;
              return;
            }
            
            // Get current device ID (may have changed after reconnect)
            const retryDeviceId = activeDeviceIdRef.current || deviceId;
            console.log(`🔄 Retry ${retryAttempt}: Using device ID: ${retryDeviceId}`);
            
            // Try to play directly
            const retryController = new AbortController();
            const retryTimeoutId = setTimeout(() => retryController.abort(), 10000);
            let retryPlayResponse;
            try {
              retryPlayResponse = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${retryDeviceId}`, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  uris: [trackUri]
                }),
                signal: retryController.signal
              });
              clearTimeout(retryTimeoutId);
            } catch (retryFetchErr) {
              clearTimeout(retryTimeoutId);
              if (retryFetchErr.name === 'AbortError') {
                console.error('❌ Retry play request timed out');
                continue; // Try next retry
              }
              throw retryFetchErr;
            }
            
            if (retryPlayResponse.ok || retryPlayResponse.status === 204) {
              console.log('✅ Track playing after retry!');
              setError(null);
              setSessionEstablished(true);
              lastPlayedUri.current = trackUri;
              isPlayingRef.current = false; // Reset playing flag on success
              success = true;
              break; // Exit retry loop on success
            } else if (retryPlayResponse.status === 404 && retryAttempt < maxRetries) {
              console.log(`⏳ Retry ${retryAttempt} also got 404, will try again...`);
              // Continue to next retry
            } else {
              const retryErrorData = await retryPlayResponse.json().catch(() => ({}));
              console.error(`❌ Retry ${retryAttempt} failed:`, retryPlayResponse.status, retryErrorData);
            }
          }
          
          if (!success) {
            // All retries failed
            console.error('❌ All retry attempts failed with 404');
            setError('Failed to establish session after retries. Please try again.');
            setSessionEstablished(false);
            isPlayingRef.current = false;
            lastPlayedUri.current = null;
            return;
          }
          
          return; // Successfully played after retry
        }
        
        // Handle other errors (non-404)
        setError(`Failed to play track (${playResponse.status}). Try clicking again or check if Spotify is open elsewhere.`);
        isPlayingRef.current = false;
        lastPlayedUri.current = null;
      } catch (err) {
        console.error('Error playing track:', err);
        setError('Failed to play track. Make sure Spotify is not playing elsewhere.');
        isPlayingRef.current = false; // Reset playing flag on error
        lastPlayedUri.current = null; // Clear last played URI on error
      } finally {
        // Cleanup
        // This ensures the indicator doesn't get stuck
        if (isPlayingRef.current && lastPlayedUri.current !== trackUri) {
        }
      }
    };

    playTrack();
    
    // Cleanup function to cancel in-flight playback if trackUri changes
    return () => {
      // Mark that this effect is being cleaned up
      currentTrackUriRef.value = null;
    };
  }, [trackUri, isReady, player, deviceId]); // Re-added deviceId but with early check to prevent unnecessary re-runs

  // Update position periodically
  useEffect(() => {
    if (!isPaused && player) {
      positionInterval.current = setInterval(async () => {
        try {
          const state = await player.getCurrentState();
          if (state) {
            setCurrentPosition(state.position);
          }
        } catch (err) {
          // Silently handle connection errors - SDK may have internal connection issues
          // but playback can still continue. Only log if it's not a connection error.
          if (err.message && !err.message.includes('CONNECTION') && !err.message.includes('connection')) {
            console.log('⚠️ Error getting player state:', err);
          }
        }
      }, 1000);
    } else {
      if (positionInterval.current) {
        clearInterval(positionInterval.current);
      }
    }

    return () => {
      if (positionInterval.current) {
        clearInterval(positionInterval.current);
      }
    };
  }, [isPaused, player]);

  // Player controls
  const togglePlay = () => {
    if (player) {
      player.togglePlay();
    }
  };

  const seek = (position) => {
    if (player) {
      player.seek(position);
      setCurrentPosition(position);
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (player) {
      player.setVolume(newVolume / 100);
    }
  };

  // Format time
  const formatTime = (ms) => {
    if (!ms) return '0:00';
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Not premium - show message
  if (isPremium === false) {
    return (
      <div className="spotify-player not-premium">
        <div className="premium-required">
          <span className="premium-icon">👑</span>
          <p>Spotify Premium required for full playback</p>
          <p className="premium-note">30-second previews available for free accounts</p>
        </div>
      </div>
    );
  }

  // Loading
  if ((!isReady || !sessionEstablished) && isPremium) {
    return (
      <div className="spotify-player loading">
        <div className="loading-spinner">🎵</div>
        <p>{!isReady ? 'Connecting to Spotify...' : 'Establishing player session...'}</p>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="spotify-player error">
        <p>⚠️ {error}</p>
      </div>
    );
  }

  return (
    <div className="spotify-player">
      {/* Track Info */}
      {currentTrack && (
        <div className="sp-track-info">
          {currentTrack.image && (
            <img src={currentTrack.image} alt="" className="sp-track-image" />
          )}
          <div className="sp-track-details">
            <p className="sp-track-name">{currentTrack.name}</p>
            <p className="sp-track-artist">{currentTrack.artist}</p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="sp-controls">
        <button 
          className="sp-btn" 
          onClick={onPrevious}
          disabled={!onPrevious}
        >
          ⏮
        </button>
        
        <button 
          className="sp-btn sp-btn-play" 
          onClick={togglePlay}
        >
          {isPaused ? '▶' : '⏸'}
        </button>
        
        <button 
          className="sp-btn" 
          onClick={onNext}
          disabled={!onNext}
        >
          ⏭
        </button>
      </div>

      {/* Progress */}
      <div className="sp-progress">
        <span className="sp-time">{formatTime(currentPosition)}</span>
        <input
          type="range"
          className="sp-progress-bar"
          min="0"
          max={duration || 0}
          value={currentPosition}
          onChange={(e) => seek(parseInt(e.target.value))}
        />
        <span className="sp-time">{formatTime(duration)}</span>
      </div>

      {/* Volume */}
      <div className="sp-volume">
        <span className="sp-volume-icon">
          {volume === 0 ? '🔇' : volume < 50 ? '🔉' : '🔊'}
        </span>
        <input
          type="range"
          className="sp-volume-bar"
          min="0"
          max="100"
          value={volume}
          onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
        />
      </div>

      {/* Queue Info */}
      {queueLength > 0 && (
        <div className="sp-queue-info">
          {currentIndex + 1} of {queueLength}
        </div>
      )}
    </div>
  );
}

export default SpotifyPlayer;

